import { useMemo, useState } from "react";
import { classes, ClassItem } from "@/data/classes";
import ClassDetailDialog from "@/components/ClassDetailDialog";
import { Clock } from "lucide-react";
import { getClassSessionSlots, type DayName } from "@/lib/sessionSchedule";
import { useClassRegistry, slugOf } from "@/lib/classRegistry";
import { useDbClasses } from "@/hooks/useDbClasses";
import { migratedSlugs } from "@/data/migrationFlags";
import { toPublicClasses } from "@/lib/publicSessions";

const days: DayName[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface CalendarEntry {
  classItem: ClassItem;
  timeDisplay: string;
  timeMinutes: number;
  sessionLabels: string[];
}

const CalendarPage = () => {
  const [selected, setSelected] = useState<ClassItem | null>(null);
  const registry = useClassRegistry();
  const dbClasses = useDbClasses();

  const dayMap = useMemo(() => {
    const map: Record<DayName, CalendarEntry[]> = {
      Sunday: [], Monday: [], Tuesday: [], Wednesday: [],
      Thursday: [], Friday: [], Saturday: [],
    };
    // Phase D: drop migrated static entries; DB rows own their content.
    const staticVisible = classes
      .filter((c) => !migratedSlugs.has(slugOf(c)))
      .filter((c) => registry.isCalendarVisible(slugOf(c)));
    const dbVisible = dbClasses.filter((c) => registry.isCalendarVisible(slugOf(c)));
    // Finished sessions are hidden from the public weekly schedule.
    const visible: ClassItem[] = toPublicClasses([...staticVisible, ...dbVisible]);
    for (const c of visible) {
      for (const slot of getClassSessionSlots(c)) {
        map[slot.weekday]?.push({
          classItem: c,
          timeDisplay: slot.timeDisplay,
          timeMinutes: slot.timeMinutes,
          sessionLabels: slot.sessionLabels,
        });
      }
    }
    for (const key of Object.keys(map) as DayName[]) {
      map[key].sort((a, b) => a.timeMinutes - b.timeMinutes);
    }
    return map;
  }, [registry.entries, dbClasses]);

  return (
    <div className="pt-24 pb-16 px-4">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl font-bold text-foreground mb-3">Weekly Schedule</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            See what's happening each day. Click any class for details.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {days.map((day) => (
            <div key={day} className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="bg-primary text-primary-foreground px-4 py-3 font-heading font-bold text-center">
                {day}
              </div>
              <div className="p-3 space-y-3 min-h-[200px]">
                {dayMap[day].length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center mt-8">No classes</p>
                ) : (
                  dayMap[day].map((entry, i) => (
                    <button
                      key={`${entry.classItem.id}-${entry.timeDisplay}-${i}`}
                      onClick={() => setSelected(entry.classItem)}
                      className="w-full text-left bg-background rounded-md p-3 border border-border hover:border-primary transition-colors cursor-pointer"
                    >
                      <p className="font-body font-bold text-sm text-foreground">{entry.classItem.title}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Clock size={10} />
                        {entry.timeDisplay || "Time TBA"}
                      </div>
                      <p className="text-xs text-primary mt-1">Ages {entry.classItem.ages}</p>
                      {entry.sessionLabels.length > 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {entry.sessionLabels.join(" · ")}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ClassDetailDialog classItem={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
};

export default CalendarPage;
